import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useMerchant } from '../../contexts/MerchantContext';
import { useSubscription } from '../../contexts/SubscriptionContext';

const PAYWALL_EXEMPT_PATHS = [
  '/admin/subscription',
  '/admin/billing',
  '/trial-expired',
  '/subscription-suspended',
];

const SUBSCRIPTION_ONLY_PATHS = [
  '/admin/subscription',
  '/admin/billing',
  '/trial-expired',
  '/subscription-suspended',
];

const MerchantRequiredRoute = ({ children }) => {
  const { isAuthenticated, hasMerchant, loading: authLoading } = useAuth();
  const { loading: merchantLoading } = useMerchant();
  const {
    subscription,
    loading: subLoading,
    isActive,
    enforcementEnabled,
  } = useSubscription();
  const location = useLocation();

  // Wait until auth, merchant, and subscription/feature-flag state have all
  // resolved before making any redirect decision. Acting on partial state
  // here is what bounced deep links to /admin on refresh.
  if (authLoading || merchantLoading || subLoading) {
    return null;
  }

  if (!isAuthenticated()) {
    const redirect = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?redirect=${redirect}`} replace />;
  }

  if (!hasMerchant()) {
    return <Navigate to="/setup-merchant" replace />;
  }

  if (!enforcementEnabled) {
    const isSubscriptionOnlyPage = SUBSCRIPTION_ONLY_PATHS.some((p) =>
      location.pathname.startsWith(p),
    );
    if (isSubscriptionOnlyPage) {
      return <Navigate to="/admin" replace />;
    }
    return children;
  }

  const isExemptPage = PAYWALL_EXEMPT_PATHS.some((p) => location.pathname.startsWith(p));

  if (!isExemptPage) {
    if (!subscription || !isActive()) {
      const isSuspended = subscription?.status === 'suspended';
      const isCancelled = subscription?.status === 'cancelled';

      if (isSuspended || isCancelled) {
        return <Navigate to="/subscription-suspended" replace />;
      }
      return <Navigate to="/trial-expired" replace />;
    }
  }

  return children;
};

export default MerchantRequiredRoute;
